/**
 * Search/typeahead team picker for `all`-mode demo orgs (e.g. Arsenal demo).
 *
 * Backend's `selectableTeams` query in `all` mode requires a non-empty
 * `query` and case-insensitive substring matches against `team.name`.
 * Skip the query while the input is empty so the backend doesn't reject
 * with `BadRequestException`.
 * @module features/team-selection/components/TeamSelectorAll
 */
import { CheckIcon } from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";

import TeamSelectorAllView, { TeamSelectorAllRow } from "./TeamSelectorAllView";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

import { formatCountrySegment } from "@/features/team-selection/utils/countryFlag";
import {
  useTeamSelectionSelectableTeamsQuery,
  type TeamSelectionTeamFragment,
} from "@/generated/graphql";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";

/**
 * Optional props for the all-mode picker container.
 */
interface Props {
  /** Optional callback fired after a successful pick (e.g. close modal). */
  readonly onSelected?: () => void;
}

/**
 * Resolves the inline list-empty message for the current state.
 * @param isIdle - True when the search input is empty
 * @param isLoading - True while the query is in flight
 * @param hasError - True if the selectableTeams query errored
 * @returns The text to render in the empty slot, or `null` when a spinner replaces the text
 */
const resolveEmptyMessage = (
  isIdle: boolean,
  isLoading: boolean,
  hasError: boolean
): string | null => {
  if (isIdle) return "Type a team name to search.";
  if (isLoading) return null;
  if (hasError) return "Couldn't load teams. Try again.";
  return "No teams match that search.";
};

/**
 * Builds the secondary "<League> · <flag> <Country>" line shown beneath the
 * team name. Returns null when neither league name nor country is known so
 * the row collapses gracefully back to a single line.
 * @param league - The team's league fragment (may be null)
 * @returns Joined league + country string, or null when nothing to show
 */
const buildLeagueSubtitle = (
  league: TeamSelectionTeamFragment["league"]
): string | null => {
  if (!league) return null;
  const segments = [league.name, formatCountrySegment(league.country)].filter(
    (segment): segment is string => Boolean(segment)
  );
  return segments.length > 0 ? segments.join(" · ") : null;
};

/**
 * Container for the all-mode picker. Owns the search input state, drives
 * the `selectableTeams` query, and routes selection through `switchTeam`.
 * @param root0 - Component props
 * @param root0.onSelected - Optional callback fired after a successful pick
 * @returns The rendered all-mode picker
 */
const TeamSelectorAllContainer = ({ onSelected }: Props) => {
  const { activeTeamId, switchTeam } = useTeamSelection();
  const [query, setQuery] = useState("");

  const trimmedQuery = query.trim();
  const skipQuery = trimmedQuery.length === 0;

  const { data, loading, error } = useTeamSelectionSelectableTeamsQuery({
    variables: { query: trimmedQuery, first: 20 },
    skip: skipQuery,
  });

  const handleSelect = useCallback(
    async (teamId: string, teamName: string) => {
      // No-op when the user re-selects the active team — switchTeam
      // would otherwise reset the Apollo store and fire a duplicate
      // `team_switched` event for a non-switch.
      if (teamId === activeTeamId) {
        onSelected?.();
        return;
      }
      await switchTeam(teamId, teamName);
      onSelected?.();
    },
    [activeTeamId, switchTeam, onSelected]
  );

  const rows = useMemo<readonly TeamSelectorAllRow[]>(() => {
    const edges = data?.selectableTeams.edges ?? [];
    return edges.map(edge => ({
      id: edge.node.id,
      name: edge.node.name,
      isActive: edge.node.id === activeTeamId,
      subtitle: buildLeagueSubtitle(edge.node.league),
      onPress: () => handleSelect(edge.node.id, edge.node.name),
    }));
  }, [data, activeTeamId, handleSelect]);

  const isLoading = loading && !skipQuery;
  const hasError = Boolean(error);

  const renderItem = useCallback(
    (item: TeamSelectorAllRow) => (
      <Pressable
        testID={`team-selector-all-option-${item.id}`}
        accessibilityRole="button"
        accessibilityLabel={item.name}
        accessibilityState={{ selected: item.isActive }}
        onPress={item.onPress}
      >
        <Stack
          direction="horizontal"
          className="items-center justify-between bg-surface-base p-3"
        >
          <Stack className="flex-1 gap-1">
            <Text
              variant="unstyled"
              className={
                item.isActive
                  ? "font-heading text-content-primary"
                  : "text-content-secondary"
              }
            >
              {item.name}
            </Text>
            {item.subtitle ? (
              <Text
                variant="caption"
                className="text-content-muted"
                numberOfLines={1}
              >
                {item.subtitle}
              </Text>
            ) : null}
          </Stack>
          {item.isActive ? (
            <Icon
              as={CheckIcon}
              className="text-needs-semantic-primary-500"
              size="sm"
            />
          ) : null}
        </Stack>
      </Pressable>
    ),
    []
  );

  const emptyMessage = resolveEmptyMessage(skipQuery, isLoading, hasError);
  const showSpinner = isLoading && !skipQuery;

  const empty = (
    <Box className="items-center py-6">
      {showSpinner ? (
        <Spinner />
      ) : (
        <Text
          variant="unstyled"
          className={hasError ? "text-status-error" : "text-content-muted"}
        >
          {emptyMessage}
        </Text>
      )}
    </Box>
  );

  return (
    <TeamSelectorAllView
      query={query}
      onChangeQuery={setQuery}
      rows={rows}
      renderItem={renderItem}
      empty={empty}
    />
  );
};

export default TeamSelectorAllContainer;
